import { useState } from "preact/hooks"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { Loader2 } from "lucide-react"
import { PromotionEffectivenessResponse, PromotionRankMetric } from "../../../../../api/promotion_effectiveness"
import { Promotion } from "../../../promo_creation/types"
import { Button } from "../../../../../components/ui/button"
import { Input } from "../../../../../components/ui/input"
import { Label } from "../../../../../components/ui/label"
import { build_peer_scatter_rows, build_period_chart_rows, cases_value, compact_number, has_case_split, money_value } from "./promotion_effectiveness_utils"
import { PromotionBumpChart } from "./PromotionBumpChart"
import { default_metric_input, parse_money_input } from "./promotion_performance_editor_utils"
import { PerformanceEditorFocus } from "./promotion_performance_editor_utils"

interface PromotionEffectivenessChartsProps {
  data: PromotionEffectivenessResponse
  rank_metric: PromotionRankMetric
  promotion: Promotion
  on_save_planned: (amount: number | null) => Promise<void>
  on_save_sales_metrics: (input: {
    period_start: string
    period_end: string
    baseline: number | null
    incremental: number | null
    total: number | null
  }) => Promise<void>
  on_match_deductions: () => void
  on_edit_metrics: (focus: PerformanceEditorFocus) => void
}

export function PromotionEffectivenessCharts({
  data,
  rank_metric,
  promotion,
  on_save_planned,
  on_save_sales_metrics,
  on_match_deductions,
  on_edit_metrics,
}: PromotionEffectivenessChartsProps) {
  const period_rows = build_period_chart_rows(data.periods)
  const peer_rows = build_peer_scatter_rows(data.peers)
  const has_spend_data = data.summary.data_coverage.has_planned_spend || data.summary.data_coverage.has_actual_spend
  const has_sales_split = data.periods.some((period) => period.baseline_sales != null && period.incremental_sales != null)
  const has_case_volume_split = !has_sales_split && has_case_split(data.periods)
  const has_peer_scatter = data.peers.some((peer) => peer.incremental_sales != null && peer.actual_spend !== 0)
  const variance_rows = [
    { name: "Planned", value: data.summary.planned_spend },
    { name: "Variance", value: data.summary.spend_variance },
    { name: "Actual", value: data.summary.actual_spend },
  ]

  return (
    <div class="grid gap-5 xl:grid-cols-2">
      <ChartPanel
        title="Plan vs actual spend"
        on_edit={has_spend_data ? () => on_edit_metrics("all") : undefined}
        edit_label="Edit spend">
        {has_spend_data ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={variance_rows} margin={{ top: 10, right: 20, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(value: number) => money_value(value)} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value: number) => money_value(value)} />
              <Bar dataKey="value">
                {variance_rows.map((row: { name: string; value: number }) => (
                  <Cell key={row.name} fill={row.name === "Variance" && row.value > 0 ? "#dc2626" : "#2563eb"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <SpendInlineEntry
            planned_amount={promotion.planned_amount ?? null}
            on_save_planned={on_save_planned}
            on_match_deductions={on_match_deductions}
          />
        )}
      </ChartPanel>

      <ChartPanel
        title={has_case_volume_split ? "Baseline and incremental cases (plan)" : "Baseline and incremental sales"}
        on_edit={has_sales_split || has_case_volume_split ? () => on_edit_metrics("sales") : undefined}
        edit_label="Edit metrics">
        {has_sales_split ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={period_rows} margin={{ top: 10, right: 20, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(value: number) => money_value(value)} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value: number) => money_value(value)} />
              <Legend />
              <Bar dataKey="baseline_sales" stackId="sales" fill="#94a3b8" name="Baseline" />
              <Bar dataKey="incremental_sales" stackId="sales" fill="#16a34a" name="Incremental" />
            </BarChart>
          </ResponsiveContainer>
        ) : has_case_volume_split ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={period_rows} margin={{ top: 10, right: 20, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(value: number) => compact_number(value)} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value: number) => cases_value(value)} />
              <Legend />
              <Bar dataKey="baseline_units" stackId="cases" fill="#94a3b8" name="Baseline cases" />
              <Bar dataKey="incremental_units" stackId="cases" fill="#16a34a" name="Incremental cases" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <SalesInlineEntry promotion={promotion} on_save={on_save_sales_metrics} />
        )}
      </ChartPanel>

      <ChartPanel title="Peer spend vs incremental sales">
        {has_peer_scatter ? (
          <ResponsiveContainer width="100%" height={260}>
            <ScatterChart margin={{ top: 10, right: 20, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="actual_spend" name="Actual spend" tickFormatter={(value: number) => money_value(value)} tick={{ fontSize: 12 }} />
              <YAxis dataKey="incremental_sales" name="Incremental sales" tickFormatter={(value: number) => money_value(value)} tick={{ fontSize: 12 }} />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                formatter={(value: number, name: string) => name === "roi" ? value?.toFixed(2) : money_value(value)}
              />
              <Scatter data={peer_rows} fill="#2563eb" />
            </ScatterChart>
          </ResponsiveContainer>
        ) : (
          <ChartRequirement
            title="Peer spend and lift data needed"
            description="Match deductions for actual spend and enter incremental sales for this promotion. Peer promotions also need metrics to populate this chart."
            actions={
              <div class="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => on_edit_metrics("sales")}>Enter sales metrics</Button>
                <Button variant="outline" onClick={on_match_deductions}>Match deductions</Button>
              </div>
            }
          />
        )}
      </ChartPanel>

      <ChartPanel title="Peer rank over time">
        <PromotionBumpChart points={data.bump_chart} rank_metric={rank_metric} />
        {!has_peer_scatter ? (
          <p class="mt-3 text-xs text-gray-500">Peer ranking fills in once this promotion and peer promotions have spend and lift metrics.</p>
        ) : null}
      </ChartPanel>
    </div>
  )
}

interface SpendInlineEntryProps {
  planned_amount: number | null
  on_save_planned: (amount: number | null) => Promise<void>
  on_match_deductions: () => void
}

function SpendInlineEntry({ planned_amount, on_save_planned, on_match_deductions }: SpendInlineEntryProps) {
  const [draft, set_draft] = useState(planned_amount == null ? "" : String(planned_amount))
  const [saving, set_saving] = useState(false)

  async function handle_save() {
    set_saving(true)
    try {
      await on_save_planned(parse_money_input(draft))
    } finally {
      set_saving(false)
    }
  }

  return (
    <ChartRequirement
      title="Spend data needed"
      description="Enter planned spend here and match deductions to populate actual spend."
      actions={
        <div class="mt-3 space-y-3">
          <div>
            <Label className="mb-1 block text-xs font-medium uppercase text-gray-500">Planned amount ($)</Label>
            <Input type="number" step="0.01" value={draft} onInput={(event: Event) => set_draft((event.target as HTMLInputElement).value)} />
          </div>
          <div class="flex flex-wrap gap-2">
            <Button onClick={handle_save} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save planned spend
            </Button>
            <Button variant="outline" onClick={on_match_deductions}>Match deductions</Button>
          </div>
        </div>
      }
    />
  )
}

interface SalesInlineEntryProps {
  promotion: Promotion
  on_save: (input: {
    period_start: string
    period_end: string
    baseline: number | null
    incremental: number | null
    total: number | null
  }) => Promise<void>
}

function SalesInlineEntry({ promotion, on_save }: SalesInlineEntryProps) {
  const defaults = default_metric_input(promotion)
  const [baseline, set_baseline] = useState("")
  const [incremental, set_incremental] = useState("")
  const [total, set_total] = useState("")
  const [period_start, set_period_start] = useState(defaults.period_start ?? "")
  const [period_end, set_period_end] = useState(defaults.period_end ?? "")
  const [saving, set_saving] = useState(false)

  async function handle_save() {
    if (!period_start || !period_end) return
    set_saving(true)
    try {
      await on_save({
        period_start,
        period_end,
        baseline: parse_money_input(baseline),
        incremental: parse_money_input(incremental),
        total: parse_money_input(total),
      })
    } finally {
      set_saving(false)
    }
  }

  return (
    <ChartRequirement
      title="Baseline and lift data needed"
      description="Enter baseline and incremental sales for the promotion period."
      actions={
        <div class="mt-3 grid gap-3 md:grid-cols-2">
          <div>
            <Label className="mb-1 block text-xs font-medium uppercase text-gray-500">Period start</Label>
            <Input type="date" value={period_start} onInput={(event: Event) => set_period_start((event.target as HTMLInputElement).value)} />
          </div>
          <div>
            <Label className="mb-1 block text-xs font-medium uppercase text-gray-500">Period end</Label>
            <Input type="date" value={period_end} onInput={(event: Event) => set_period_end((event.target as HTMLInputElement).value)} />
          </div>
          <div>
            <Label className="mb-1 block text-xs font-medium uppercase text-gray-500">Baseline sales ($)</Label>
            <Input type="number" step="0.01" value={baseline} onInput={(event: Event) => set_baseline((event.target as HTMLInputElement).value)} />
          </div>
          <div>
            <Label className="mb-1 block text-xs font-medium uppercase text-gray-500">Incremental sales ($)</Label>
            <Input type="number" step="0.01" value={incremental} onInput={(event: Event) => set_incremental((event.target as HTMLInputElement).value)} />
          </div>
          <div class="md:col-span-2">
            <Label className="mb-1 block text-xs font-medium uppercase text-gray-500">Total sales ($) optional</Label>
            <Input type="number" step="0.01" value={total} onInput={(event: Event) => set_total((event.target as HTMLInputElement).value)} />
          </div>
          <div class="md:col-span-2">
            <Button onClick={handle_save} disabled={saving || !period_start || !period_end}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save sales metrics
            </Button>
          </div>
        </div>
      }
    />
  )
}

function ChartPanel({
  title,
  children,
  on_edit,
  edit_label,
}: {
  title: string
  children: preact.ComponentChildren
  on_edit?: () => void
  edit_label?: string
}) {
  return (
    <div class="rounded-md border border-gray-200 p-4">
      <div class="mb-3 flex items-center justify-between gap-2">
        <h4 class="text-sm font-semibold text-gray-900">{title}</h4>
        {on_edit ? (
          <Button variant="ghost" className="h-8 px-2 text-xs" onClick={on_edit}>
            {edit_label ?? "Edit"}
          </Button>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function ChartRequirement({
  title,
  description,
  actions,
}: {
  title: string
  description: string
  actions?: preact.ComponentChildren
}) {
  return (
    <div class="flex min-h-[260px] flex-col justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 p-5 text-sm text-gray-700">
      <div class="font-medium text-gray-900">{title}</div>
      <div class="mt-2 text-gray-600">{description}</div>
      {actions}
    </div>
  )
}
